import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n, formatPrice } from "../i18n";
import { fetchCars } from "../api";
import { ArrowRight, Calendar as CalendarIcon } from "lucide-react";

/**
 * Hero quick-quote widget.
 * Date inputs + category select → instantly shows the cheapest car
 * for that category and the projected total for the date range,
 * then navigates to /fleet with the filter applied.
 */
const QuickQuote = () => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [pickup, setPickup] = useState("");
  const [ret, setRet] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    fetchCars().then(setCars).catch(() => setCars([]));
  }, []);

  const days = useMemo(() => {
    if (!pickup || !ret) return 0;
    const ms = new Date(ret) - new Date(pickup);
    return Math.max(0, Math.round(ms / 86400000));
  }, [pickup, ret]);

  const cheapest = useMemo(() => {
    const list =
      category === "all" ? cars : cars.filter((c) => c.category === category);
    if (!list.length) return null;
    return list.reduce((a, b) => (a.price_per_day <= b.price_per_day ? a : b));
  }, [cars, category]);

  const total = cheapest && days > 0 ? cheapest.price_per_day * days : 0;
  const today = new Date().toISOString().slice(0, 10);

  const goToFleet = () => {
    if (cheapest && pickup && ret && days > 0) {
      navigate(`/booking/${cheapest.id}`);
    } else {
      navigate(category === "all" ? "/fleet" : `/fleet?cat=${category}`);
    }
  };

  const categories = ["all", "economy", "compact", "midsize", "suv", "minivan", "van"];
  const isPt = lang === "pt";

  return (
    <div
      className="bg-[#0a0a0a]/85 backdrop-blur-md border border-white/15 p-5 md:p-6 mt-10 grid grid-cols-1 md:grid-cols-12 gap-4 items-end max-w-4xl"
      data-testid="quick-quote-widget"
      style={{ boxShadow: "0 30px 60px -25px rgba(255, 180, 0, 0.25)" }}
    >
      <div className="md:col-span-3">
        <label className="text-label block mb-2">
          <CalendarIcon size={11} className="inline mr-1.5 -mt-0.5" />
          {t.booking.pickupDate}
        </label>
        <input
          type="date"
          min={today}
          value={pickup}
          onChange={(e) => setPickup(e.target.value)}
          className="movix-input text-sm"
          data-testid="qq-pickup"
        />
      </div>

      <div className="md:col-span-3">
        <label className="text-label block mb-2">
          <CalendarIcon size={11} className="inline mr-1.5 -mt-0.5" />
          {t.booking.returnDate}
        </label>
        <input
          type="date"
          min={pickup || today}
          value={ret}
          onChange={(e) => setRet(e.target.value)}
          className="movix-input text-sm"
          data-testid="qq-return"
        />
      </div>

      <div className="md:col-span-3">
        <label className="text-label block mb-2">
          {isPt ? "Categoria" : "Category"}
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="movix-input text-sm appearance-none cursor-pointer"
          data-testid="qq-category"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat} className="bg-[#0a0a0a]">
              {t.fleet.filters[cat]}
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-3">
        <button
          onClick={goToFleet}
          className="btn-primary w-full justify-center"
          data-testid="qq-cta"
        >
          {isPt ? "Buscar" : "Search"}
          <ArrowRight size={16} />
        </button>
      </div>

      {days > 0 && cheapest && (
        <div
          className="md:col-span-12 mt-1 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3"
          data-testid="qq-preview"
        >
          <div className="text-sm">
            <span className="text-zinc-400 font-mono-ui tracking-widest uppercase text-xs mr-2">
              {isPt ? "A partir de" : "From"}
            </span>
            <span className="text-white font-heading font-bold text-2xl">
              {cheapest.name}
            </span>
            <span className="text-zinc-500 text-xs ml-2 font-mono-ui uppercase tracking-widest">
              · {days} {days === 1 ? t.common.day : t.common.days}
            </span>
          </div>
          <div className="text-right">
            <div className="text-label text-zinc-500">{t.common.total}</div>
            <div className="font-heading text-3xl font-bold text-[#FFB400]">
              {formatPrice(total)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickQuote;
